import { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Lock, Clock, GraduationCap, Award, BookOpen, CheckCircle, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AcademySkeleton } from '@/components/skeletons/AcademySkeleton';
import LessonCard from '@/components/academy/LessonCard';
import TaskSection from '@/components/academy/TaskSection';
import LockedModuleOverlay from '@/components/academy/LockedModuleOverlay';
import { useSubscription } from '@/hooks/useSubscription';
import { useAuth } from '@/hooks/useAuth';
import { useAcademyProgress } from '@/hooks/useAcademyProgress';
import { ACADEMY_MODULES } from '@/data/academyModules';

const SECRET_MODULE_ID = 11;
// TEMP: stała data odblokowania zamiast logiki created_at — przywrócić po testach
const SECRET_UNLOCK_DATE = new Date('2026-07-30');

export const POSITION_KEY = 'viraluj-position';
const WELCOME_KEY = 'viraluj-academy-started';

export const loadPosition = (): { moduleId: number; lessonIndex: number } => {
  try {
    const parsed = JSON.parse(localStorage.getItem(POSITION_KEY) || '{}');
    const validIds = ACADEMY_MODULES.map(m => m.id);
    const moduleId = validIds.includes(parsed.moduleId) ? parsed.moduleId : 1;
    const lessonIndex = typeof parsed.lessonIndex === 'number' && parsed.lessonIndex >= 0
      ? parsed.lessonIndex
      : 0;
    return { moduleId, lessonIndex };
  } catch {
    return { moduleId: 1, lessonIndex: 0 };
  }
};

const Academy = () => {
  const { subscribed, isTrial, loading } = useSubscription();
  const { user } = useAuth();
  const { progress, isLoading: progressLoading, completeModule } = useAcademyProgress(user?.id ?? null);
  const savedPosition = loadPosition();
  const [activeId, setActiveId] = useState(savedPosition.moduleId);
  const [lessonIndex, setLessonIndex] = useState(savedPosition.lessonIndex);

  const hasFullAccess = subscribed && !isTrial;
  const topRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const [navHeight, setNavHeight] = useState(0);
  const [view, setView] = useState<'welcome' | 'modules'>(() => {
    try { return localStorage.getItem(WELCOME_KEY) === 'true' ? 'modules' : 'welcome'; }
    catch { return 'welcome'; }
  });

  const startAcademy = () => {
    localStorage.setItem(WELCOME_KEY, 'true');
    setView('modules');
  };

  // scrollIntoView({block:'start'}) lands topRef behind the sticky DashboardHeader (64px).
  // Scrolling the layout's overflow-y-auto container to 0 avoids that offset entirely.
  const scrollToTop = () => {
    const container = topRef.current?.closest('.overflow-y-auto') as HTMLElement | null;
    container?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setNavHeight(entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const daysUntilSecret = Math.max(0, Math.ceil((SECRET_UNLOCK_DATE.getTime() - Date.now()) / 86_400_000));
  const isSecretUnlocked = daysUntilSecret === 0;

  useEffect(() => {
    localStorage.setItem(POSITION_KEY, JSON.stringify({ moduleId: activeId, lessonIndex }));
  }, [activeId, lessonIndex]);

  const switchModule = (id: number) => {
    setActiveId(id);
    setLessonIndex(0);
    scrollToTop();
  };

  const goNextLesson = () => {
    scrollToTop();
    setLessonIndex(i => i + 1);
  };

  const goPrevLesson = () => {
    scrollToTop();
    setLessonIndex(i => i - 1);
  };

  const completedCount = Object.values(progress).filter(Boolean).length;
  const progressPct = Math.round((completedCount / ACADEMY_MODULES.length) * 100);

  const activeModule = ACADEMY_MODULES.find(m => m.id === activeId)!;
  const isLocked = !hasFullAccess && activeId !== 1;
  const isSecretLocked = activeId === SECRET_MODULE_ID && !isSecretUnlocked;

  if (loading || progressLoading) return <AcademySkeleton />;

  if (view === 'welcome') {
    return (
      <main className="px-4 md:px-6 pb-16 flex justify-center">
        <div className="w-full max-w-[600px] py-8 md:py-12 flex flex-col items-center text-center">

          {/* Ikona */}
          <div className="relative mb-6">
            <div className="absolute inset-0 rounded-full bg-orange-500/10 scale-150 animate-pulse" />
            <div className="relative w-20 h-20 rounded-2xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg shadow-orange-500/25">
              <GraduationCap className="w-10 h-10 text-white" />
            </div>
          </div>

          {/* Tytuł */}
          <div className="inline-flex items-center gap-1.5 bg-orange-500/10 text-orange-600 dark:text-orange-400 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wide mb-3">
            Szkolenie Viraluj
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-foreground leading-tight mb-3">
            Akademia <span className="text-orange-500">Instagrama</span>
          </h1>
          <p className="text-base text-muted-foreground max-w-sm leading-relaxed mb-8">
            Kompletny kurs prowadzący Cię krok po kroku — od pustego profilu do regularnie rosnącego konta z zaangażowaną społecznością.
          </p>

          {/* Cechy */}
          <div className="w-full grid grid-cols-1 gap-3 mb-8 text-left">
            {[
              { icon: BookOpen, label: '11 modułów i 30+ lekcji', desc: 'Strategia, scenariusze, napisy, SEO, rytm publikacji i analityka.' },
              { icon: CheckCircle, label: 'Praktyczne zadania', desc: 'Po każdym module konkretne ćwiczenie do wykonania na własnym profilu.' },
              { icon: Award, label: 'Certyfikat ukończenia', desc: 'Po ukończeniu wszystkich modułów otrzymasz imienny certyfikat.' },
            ].map(({ icon: Icon, label, desc }) => (
              <div key={label} className="flex items-start gap-3 rounded-xl border border-border/50 bg-card p-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center mt-0.5">
                  <Icon className="w-4 h-4 text-orange-500" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">{label}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <Button
            onClick={startAcademy}
            size="lg"
            className="w-full max-w-xs bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold text-base shadow-lg shadow-orange-500/25 gap-2"
          >
            <Play className="w-4 h-4" />
            Rozpocznij szkolenie
          </Button>

          <button
            onClick={startAcademy}
            className="mt-3 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Wróć do postępu →
          </button>

        </div>
      </main>
    );
  }

  return (
    <main className="px-3 md:px-6 pb-16 flex justify-center">
      <div ref={topRef} className="w-full max-w-[860px] pb-16">

        {/* Nav kursu: fixed na mobile (tuż pod headerem), sticky na desktop */}
        <div
          ref={navRef}
          className="fixed top-16 left-0 right-0 z-40 md:sticky md:top-16 md:relative md:left-auto md:right-auto md:z-10 bg-background/95 backdrop-blur border-b border-border/40 px-3 md:px-6 pt-2 pb-2.5 space-y-2"
        >

          {/* Pasek postępu kursu */}
          <div>
            <div className="flex justify-between text-[11px] text-muted-foreground mb-1">
              <span>Postęp kursu</span>
              <span>{completedCount}/{ACADEMY_MODULES.length} modułów</span>
            </div>
            <div className="h-1 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-orange-500 to-orange-400 rounded-full transition-all duration-500"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>

          {/* Moduły — strzałkowy slider */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => switchModule(activeId - 1)}
              disabled={activeId <= 1}
              className="flex-shrink-0 w-8 h-8 rounded-lg border border-border/60 bg-background flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            <div className="flex-1 min-w-0 text-center">
              <div className="flex items-center justify-center gap-1.5">
                {isSecretLocked ? (
                  <Clock className="w-3 h-3 text-orange-400 flex-shrink-0" />
                ) : isLocked ? (
                  <Lock className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                ) : progress[activeId] ? (
                  <span className="text-[11px] text-green-500 font-bold">✓</span>
                ) : null}
                <span className="text-[11px] font-medium text-muted-foreground">
                  Moduł {activeId}/{ACADEMY_MODULES.length}
                </span>
              </div>
              <p className="text-base md:text-lg font-bold text-foreground truncate leading-tight mt-0.5">
                {activeModule.title}
              </p>
            </div>

            <button
              onClick={() => switchModule(activeId + 1)}
              disabled={activeId >= ACADEMY_MODULES.length}
              className="flex-shrink-0 w-8 h-8 rounded-lg border border-border/60 bg-background flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Lekcje — tylko gdy moduł odblokowany */}
          {!isSecretLocked && !isLocked && (
            <div className="flex overflow-x-auto gap-1.5 scrollbar-none justify-center">
              {activeModule.lessons.map((l, i) => (
                <button
                  key={i}
                  onClick={() => { scrollToTop(); setLessonIndex(i); }}
                  title={l.title}
                  className={`h-7 flex-shrink-0 rounded-full text-[11px] font-semibold px-3 transition-all duration-200 ${
                    i < lessonIndex
                      ? 'bg-green-500/20 text-green-600 dark:text-green-400 hover:bg-green-500/30'
                      : i === lessonIndex
                      ? 'bg-orange-500 text-white'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  Lekcja {i + 1}
                </button>
              ))}
              <button
                onClick={() => { scrollToTop(); setLessonIndex(activeModule.lessons.length); }}
                className={`h-7 flex-shrink-0 rounded-full text-[11px] font-semibold px-3 transition-all duration-200 ${
                  lessonIndex === activeModule.lessons.length
                    ? 'bg-orange-500 text-white'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                Zadanie
              </button>
            </div>
          )}
        </div>

        {/* Spacer dla fixed nava na mobile — desktop nav jest w flow więc nie potrzeba */}
        <div className="md:hidden mb-4" style={{ height: navHeight }} />

        {/* Module content */}
        <div className="mt-2">
          {/* Module header — styl info card */}
          <div className="mb-5 rounded-xl border border-primary/20 bg-gradient-to-br from-primary/5 to-transparent p-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-black text-primary">{activeModule.id}</span>
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-sm font-semibold text-foreground leading-tight">
                  {activeModule.title}
                </h2>
                <p className="text-xs text-muted-foreground leading-relaxed mt-0.5 truncate">
                  {activeModule.subtitle}
                </p>
              </div>
              <div className="flex-shrink-0 text-right">
                <span className="text-xs font-semibold text-primary">
                  {Math.min(lessonIndex, activeModule.lessons.length)}/{activeModule.lessons.length}
                </span>
                <p className="text-[10px] text-muted-foreground">lekcji</p>
              </div>
            </div>
          </div>

          {isSecretLocked ? (
            <div className="relative overflow-hidden rounded-2xl border border-orange-500/25 bg-gradient-to-br from-orange-950/30 via-card to-card">
              {/* subtle bg glow */}
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(249,115,22,0.08)_0%,_transparent_70%)]" />

              <div className="relative flex flex-col items-center justify-center py-16 px-6 text-center">
                {/* animated countdown ring */}
                <div className="relative mb-8">
                  <div className="absolute inset-0 rounded-full bg-orange-500/10 animate-pulse scale-[1.35]" />
                  <div className="absolute inset-0 rounded-full border border-orange-500/15 scale-[1.18]" />
                  <div className="relative flex h-32 w-32 flex-col items-center justify-center rounded-full border border-orange-500/40 bg-gradient-to-br from-orange-500/15 to-orange-900/10 shadow-[0_0_32px_rgba(249,115,22,0.15)]">
                    <span className="text-5xl font-black leading-none text-orange-400 tabular-nums">{daysUntilSecret}</span>
                    <span className="mt-1 text-[10px] font-bold uppercase tracking-[0.15em] text-orange-400/60">
                      {daysUntilSecret === 1 ? 'dzień' : 'dni'}
                    </span>
                  </div>
                </div>

                {/* title */}
                <div className="mb-3 flex items-center gap-2">
                  <Lock className="h-4 w-4 text-orange-400/70" />
                  <h3 className="bg-gradient-to-r from-orange-300 to-orange-100 bg-clip-text text-xl font-extrabold text-transparent">
                    Sekretna wiedza
                  </h3>
                  <Lock className="h-4 w-4 text-orange-400/70" />
                </div>

                <p className="max-w-xs text-sm leading-relaxed text-muted-foreground">
                  Ten moduł odblokowuje się automatycznie po zakończeniu kursu.<br />
                  Wróć za <span className="font-semibold text-foreground">{daysUntilSecret} {daysUntilSecret === 1 ? 'dzień' : 'dni'}</span>.
                </p>
              </div>
            </div>
          ) : isLocked ? (
            <div className="border border-border/50 rounded-xl bg-card">
              <LockedModuleOverlay moduleTitle={activeModule.title} />
            </div>
          ) : (
            <>
              {lessonIndex < activeModule.lessons.length ? (
                <>
                  <LessonCard lesson={activeModule.lessons[lessonIndex]} index={lessonIndex} />
                  <div className="flex justify-between items-center mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={goPrevLesson}
                      disabled={lessonIndex === 0}
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Wstecz
                    </Button>
                    <Button
                      size="sm"
                      onClick={goNextLesson}
                      className="bg-orange-500 hover:bg-orange-600 text-white border-transparent"
                    >
                      {lessonIndex === activeModule.lessons.length - 1 ? 'Przejdź do zadania' : 'Dalej'}
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <TaskSection
                    task={activeModule.task}
                    moduleId={activeModule.id}
                    onComplete={() => completeModule(activeModule.id)}
                    isCompleted={!!progress[activeModule.id]}
                  />
                  <div className="flex justify-between items-center mt-4">
                    <Button variant="outline" size="sm" onClick={goPrevLesson}>
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Wróć do lekcji
                    </Button>
                    {activeModule.id < ACADEMY_MODULES.length && (
                      <Button
                        size="sm"
                        onClick={() => switchModule(activeModule.id + 1)}
                        className="bg-orange-500 hover:bg-orange-600 text-white border-transparent"
                      >
                        Następny moduł
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>

      </div>
    </main>
  );
};

export default Academy;
